'use client';

import { useState, useCallback, useMemo } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import type { ScreenName } from '../types/navigation';

interface TabBarState {
  manualOverride?: boolean; // If set, overrides automatic visibility
  mainScreens: ScreenName[];
}

export function useTabBar() {
  const navigation = useNavigation();

  // Get current screen
  const currentStack = navigation.state.stacks[navigation.state.activeTab];
  const currentScreen = currentStack[currentStack.length - 1]?.screen;

  // State for manual visibility override and main screens list
  const [state, setState] = useState<TabBarState>({
    mainScreens: ['balance', 'games', 'inventory', 'shop', 'referrals', 'profile'] as ScreenName[],
  });

  // Show tab bar (manual override)
  const show = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: true }));
  }, []);

  // Hide tab bar (manual override)
  const hide = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: false }));
  }, []);

  // Clear manual override
  const clearOverride = useCallback(() => {
    setState((prev) => ({ ...prev, manualOverride: undefined }));
  }, []);

  // Toggle tab bar visibility
  const toggle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      manualOverride:
        prev.manualOverride === undefined ? !state.mainScreens.includes(currentScreen) : !prev.manualOverride,
    }));
  }, [currentScreen, state.mainScreens]);

  // Set main screens that should show tab bar
  const setMainScreens = useCallback((screens: ScreenName[]) => {
    setState((prev) => ({ ...prev, mainScreens: screens }));
  }, []);

  // Add a main screen
  const addMainScreen = useCallback((screen: ScreenName) => {
    setState((prev) => ({
      ...prev,
      mainScreens: prev.mainScreens.includes(screen) ? prev.mainScreens : [...prev.mainScreens, screen],
    }));
  }, []);

  // Remove a main screen
  const removeMainScreen = useCallback((screen: ScreenName) => {
    setState((prev) => ({
      ...prev,
      mainScreens: prev.mainScreens.filter((s) => s !== screen),
    }));
  }, []);

  // Check if current screen is a main screen
  const isMainScreen = state.mainScreens.includes(currentScreen);

  // Compute visibility: manual override takes precedence, otherwise based on screen
  const visible = state.manualOverride !== undefined ? state.manualOverride : isMainScreen;

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // State
      visible,
      isMainScreen,
      currentScreen,
      mainScreens: state.mainScreens,
      manualOverride: state.manualOverride,

      // Actions
      show,
      hide,
      toggle,
      clearOverride,
      setMainScreens,
      addMainScreen,
      removeMainScreen,
    }),
    [
      visible,
      isMainScreen,
      currentScreen,
      state.mainScreens,
      state.manualOverride,
      show,
      hide,
      toggle,
      clearOverride,
      setMainScreens,
      addMainScreen,
      removeMainScreen,
    ],
  );
}
